import React, { useContext } from 'react';
import { LocaleContext } from '../../../components/LocaleContext';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { postRequest } from '../../../api/ApiController';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { SEMAFOR_COLOR_OPTIONS, getSemaforColorClass } from '../../../utils/moduleSettings';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { uiT } from '../../../utils/uiTranslate';

// Konvencia: Zapnuté (1) = áno, zobrazovať (vyradené/deaktivované/neaktívne). Vypnuté (0) = nie, skryť.
const getOptions = () => [
  { label: uiT('Common.Off'), value: '0' },
  { label: uiT('Common.On'), value: '1' },
];

// Farby pre zoznamy – buckety ako v TrainingCompanyCertStateList (0-, 15-, 30-, 45-, 60-, 61+, DEA)
const getSemaforKeys = () => [
  { key: 'trng_semafor_0', label: uiT('Common.ExpBucket.After') },
  { key: 'trng_semafor_15', label: uiT('Common.ExpBucket.15') },
  { key: 'trng_semafor_30', label: uiT('Common.ExpBucket.30') },
  { key: 'trng_semafor_45', label: uiT('Common.ExpBucket.45') },
  { key: 'trng_semafor_60', label: uiT('Common.ExpBucket.60') },
  { key: 'trng_semafor_60_plus', label: uiT('Common.ExpBucket.61plus') },
  { key: 'trng_semafor_dea', label: uiT('TrainingGeneralSettings.Text.DEA_vyradeny_certifikat') },
];

// Farby pre grafy v Náhľade školenia (4 segmenty: Po expirácii, 15 dní, 30 dní, OK)
const getGraphTrainingKeys = () => [
  { key: 'trng_graph_0', label: uiT('Common.AfterExpiration') },
  { key: 'trng_graph_15', label: uiT('Common.DaysToExp15') },
  { key: 'trng_graph_30', label: uiT('Common.DaysToExp30') },
  { key: 'trng_graph_ok', label: uiT('Common.EligibleOk') },
];

function getSetting(module_settings, key, defaultValue = '0') {
  const row = (module_settings || []).find(s => s.module === 'training' && s.key === key);
  const v = row ? row.value : defaultValue;
  if (v == null || v === '') return defaultValue;
  return (v === 0 || v === '0') ? '0' : (v === 1 || v === '1') ? '1' : String(v);
}

function getColorSetting(module_settings, key, defaultValue = 'orange') {
  const row = (module_settings || []).find(s => s.module === 'training' && s.key === key);
  const v = row ? row.value : defaultValue;
  return v != null && v !== '' ? String(v) : defaultValue;
}

export default function TrainingGeneralSettings() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { toast } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);

  const module_settings = state.module_settings || [];

  const inNotifications = getSetting(module_settings, 'show_deactivated_in_notifications', '1');
  const inLists = getSetting(module_settings, 'show_deactivated_in_lists', '1');
  const inactiveEmployeesInNotif = getSetting(module_settings, 'show_inactive_employees_in_notifications', '0');
  const inactiveEmployeesInLists = getSetting(module_settings, 'show_inactive_employees_in_lists', '0');
  const excludedCompaniesByCompany = getSetting(module_settings, 'show_excluded_training_companies_list_by_company', '0');
  const excludedCompaniesByState = getSetting(module_settings, 'show_excluded_training_companies_list_by_state', '0');

  const settingInNotif = module_settings.find(s => s.module === 'training' && s.key === 'show_deactivated_in_notifications');
  const settingInLists = module_settings.find(s => s.module === 'training' && s.key === 'show_deactivated_in_lists');
  const settingInactiveNotif = module_settings.find(s => s.module === 'training' && s.key === 'show_inactive_employees_in_notifications');
  const settingInactiveLists = module_settings.find(s => s.module === 'training' && s.key === 'show_inactive_employees_in_lists');
  const settingExcludedByCompany = module_settings.find(s => s.module === 'training' && s.key === 'show_excluded_training_companies_list_by_company');
  const settingExcludedByState = module_settings.find(s => s.module === 'training' && s.key === 'show_excluded_training_companies_list_by_state');

  async function updateSetting(setting, value, currentValue) {
    const normalized = value === 0 || value === '0' ? '0' : (value === 1 || value === '1' ? '1' : null);
    if (normalized === null) return;
    if (normalized === (currentValue === 0 || currentValue === '0' ? '0' : '1')) return;
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFoundInDb') });
      return;
    }
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value: normalized }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value: normalized });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChangedImmediate') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
    }
  }

  async function updateSemaforSetting(setting, value, currentValue) {
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
      return;
    }
    const COLOR_VALUES = ['red', 'orange', 'green', 'gray', 'blue', 'black'];
    if (!COLOR_VALUES.includes(value)) return;
    if (value === currentValue) return;
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.SemaforColorChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
    }
  }

  async function updateGraphColorSetting(setting, value, currentValue) {
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
      return;
    }
    const COLOR_VALUES = ['red', 'orange', 'green', 'gray', 'blue', 'black'];
    if (!COLOR_VALUES.includes(value)) return;
    if (value === currentValue) return;
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ColorChangedSuccess') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
    }
  }

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">{t('CustomerGeneralSettings.Title')}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full items-stretch">
          {/* Karta 1: Zobrazenie */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Zobrazenie_deaktivovanych_skoleni')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Deaktivovane_skolenia_mozete_zobrazit_alebo_skryt_v_notifika')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.V_notifikaciach')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={inNotifications}
                    onChange={(e) => updateSetting(settingInNotif, e.value, inNotifications)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">V zoznamoch:</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={inLists}
                    onChange={(e) => updateSetting(settingInLists, e.value, inLists)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">{t('Ui.R67.Zobrazenie_neaktivnych_zamestnancov')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Zamestnanci_s_ukoncenym_pracovnym_pomerom_datum_odchodu_mozu')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.V_notifikaciach')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={inactiveEmployeesInNotif}
                    onChange={(e) => updateSetting(settingInactiveNotif, e.value, inactiveEmployeesInNotif)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">V zoznamoch:</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={inactiveEmployeesInLists}
                    onChange={(e) => updateSetting(settingInactiveLists, e.value, inactiveEmployeesInLists)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">{t('Ui.R67.Zobrazenie_vyradenych_skoliacich_firiem')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Skoliace_firmy_oznacene_ako_vyradene_v_karte_firmy_sa_standa')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_podla_firmy')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={excludedCompaniesByCompany}
                    onChange={(e) => updateSetting(settingExcludedByCompany, e.value, excludedCompaniesByCompany)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_podla_stavu')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={excludedCompaniesByState}
                    onChange={(e) => updateSetting(settingExcludedByState, e.value, excludedCompaniesByState)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Karta 2: Farby pre zoznamy */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              Farby pre zoznamy
            </h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Tieto_farby_sa_pouzivaju_v_zoznamoch_certifikatov_skoliacich')}</p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {getSemaforKeys().map(({ key, label }) => {
                const setting = module_settings.find(s => s.module === 'training' && s.key === key);
                const defaultValue = key === 'trng_semafor_0' ? 'red' : (key === 'trng_semafor_dea' ? 'gray' : (key === 'trng_semafor_60_plus' ? 'green' : 'orange'));
                const currentValue = getColorSetting(module_settings, key, defaultValue);
                const colorClass = getSemaforColorClass(currentValue);
                return (
                  <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                    <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                    <div className="flex items-center gap-2 shrink-0 min-w-0">
                      <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                      <Dropdown
                        value={currentValue}
                        options={SEMAFOR_COLOR_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => updateSemaforSetting(setting, e.value, currentValue)}
                        className="w-[180px] border border-gray-400 rounded"
                        placeholder={uiT('Common.Color')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Karta 3: Farby pre grafy */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Farby_pre_grafy_v_nahlade_skolenia')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Nastavenie_farieb_grafov_na_stranke_Nahlad_skolenia_segmenty')}</p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {getGraphTrainingKeys().map(({ key, label }) => {
                const setting = module_settings.find(s => s.module === 'training' && s.key === key);
                const defaultValue = key === 'trng_graph_0' ? 'red' : (key === 'trng_graph_ok' ? 'green' : 'orange');
                const currentValue = getColorSetting(module_settings, key, defaultValue);
                const colorClass = getSemaforColorClass(currentValue);
                return (
                  <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                    <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                    <div className="flex items-center gap-2 shrink-0 min-w-0">
                      <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                      <Dropdown
                        value={currentValue}
                        options={SEMAFOR_COLOR_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => updateGraphColorSetting(setting, e.value, currentValue)}
                        className="w-[180px] border border-gray-400 rounded"
                        placeholder={uiT('Common.Color')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
